export function debounce(func, delay) {
  let timeout
  return function (...args) {
    clearTimeout(timeout)
    timeout = setTimeout(func, delay, ...args)
  }
}

export const defaultColours = {
  off: "#F5F6F6",
  info: "#2196F3",
  error: "#E10610",
  error2: "#E10655",
  accent: "#C7C9C7",
  primary: "#147BD1",
  success: "#4CAF50",
  warning: "#FB8C00",
  primary2: "#003ACD",
  success2: "#00AB11",
  secondary: "#333F48",
  card: "#E3E3E3",
  card2: "#EEEFEE",
  background1: "#50B5FF50",
  background2: "#A9A9A920",
  backgroundDir: "45deg"
}

export function prettyIDName(id) {
  if (id.actor_type === "client" || id.actor_type === "partner")
    return id.payload.name

  if (id.actor_type === "employee")
    return `${id.payload.first_name} ${id.payload.last_name}`
}

function getLuminance(r, g, b) {
  // Convert RGB values to sRGB
  r = r / 255.0
  g = g / 255.0
  b = b / 255.0

  // Apply gamma correction
  r = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4)
  g = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4)
  b = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4)

  // Calculate luminance
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

function getContrastRatio(l1, l2) {
  return (l1 + 0.05) / (l2 + 0.05) > 1
    ? (l1 + 0.05) / (l2 + 0.05)
    : (l2 + 0.05) / (l1 + 0.05)
}

export function getTextColour(bgColour) {
  // Extract RGB components from hex color
  const r = parseInt(bgColour.slice(1, 3), 16)
  const g = parseInt(bgColour.slice(3, 5), 16)
  const b = parseInt(bgColour.slice(5, 7), 16)

  const bgLuminance = getLuminance(r, g, b)

  const blackLuminance = getLuminance(0, 0, 0)
  const whiteLuminance = getLuminance(255, 255, 255)

  const contrastWithWhite = getContrastRatio(bgLuminance, whiteLuminance)

  if (contrastWithWhite > 2.5) return "white"

  const contrastWithBlack = getContrastRatio(bgLuminance, blackLuminance)

  return contrastWithBlack > contrastWithWhite ? "black" : "white"
}

export function adjustShade(hex, factor) {
  // Convert HEX to RGB
  let r = parseInt(hex.slice(1, 3), 16)
  let g = parseInt(hex.slice(3, 5), 16)
  let b = parseInt(hex.slice(5, 7), 16)

  // Adjust each component while preserving the ratio
  r = Math.min(255, Math.max(0, Math.floor(r * factor)))
  g = Math.min(255, Math.max(0, Math.floor(g * factor)))
  b = Math.min(255, Math.max(0, Math.floor(b * factor)))

  // Convert back to HEX
  const toHex = (value) => value.toString(16).padStart(2, "0")
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

export function toTitleCase(string) {
  if (!string) return
  return string
    .replaceAll("_", " ")
    .replace(
      /[a-zA-Z]+/g,
      (text) => text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
    )
}

export function capitalise(str) {
  return str?.charAt(0).toUpperCase() + str?.slice(1).toLowerCase()
}

export function getScanAnalysis(data) {
  const scanAnalysis = {
    scoreImmediate: data.scanAnalysis.scoreImmediate,
    scoreRestorative: data.scanAnalysis.scoreRestorative,
    scanGaugeComparisons: [],
    narrowScanBodyComparisons: []
  }
  data.scanAnalysis.scanGaugeComparisons?.forEach((c) =>
    scanAnalysis.scanGaugeComparisons.push({
      scoreImmediate: c.scoreImmediate,
      scoreRestorative: c.scoreRestorative
    })
  )
  data.scanAnalysis.narrowScanBodyComparisons?.forEach((c) =>
    scanAnalysis.narrowScanBodyComparisons.push({
      pass: c.pass
    })
  )
  return scanAnalysis
}
